import { useFormContext, useWatch } from "react-hook-form";
import { FormField, FormItem, FormMessage, TextField } from "@flint/ui/forms";
import { StoreFormRow } from "./store-form-section.tsx";
import { PricingEntryList } from "./pricing-entry-list.tsx";
import {
  type PricingEntry,
  entryPiecesOnHand,
  parseSizes,
  primaryEntryPieces,
} from "./size-ratio.ts";

/**
 * The pricing block of the product form: every way this product can be bought.
 *
 * Shared by the new and edit pages, which are meant to read field for field.
 * It drives itself off form context rather than props, so both callers just
 * drop it into the "Bundle Builder" card (#265).
 *
 * There is deliberately **no product-level unit of sale, base price, discount
 * or MOQ** any more (ADR-0051). All four vary between the ways a style is sold,
 * so they moved onto the entries; leaving a copy at product level would be a
 * second, rival answer to the same question.
 *
 * The one thing still handled here is a listing saved in the legacy positional
 * format (`1-2-2-1`), which can't be turned into an entry but still has a real
 * piece count to preserve.
 */
export function ProductPackFields() {
  const form = useFormContext();
  const sizeRatio = (useWatch({ control: form.control, name: "sizeRatio" }) as string) ?? "";
  const taxPct = (useWatch({ control: form.control, name: "taxPct" }) as string) ?? "";
  const entries =
    (useWatch({ control: form.control, name: "pricingEntries" }) as PricingEntry[]) ?? [];

  const legacy = primaryEntryPieces(entries) === 0 && parseSizes(sizeRatio) === null;

  // Coverage rule (ADR-0065 companion), enforced where the bundle is SAVED:
  // the bundles' total pieces must not exceed a non-blank opening stock. The
  // edit form has no `openingQty` field, so the watch comes back undefined
  // there and the guard stays silent — new-product form only.
  const openingQtyRaw =
    (useWatch({ control: form.control, name: "openingQty" }) as string | undefined) ?? "";
  const coverageGuard = (candidatePiecesOnHand: number, editIdx?: number): string | null => {
    const opening = openingQtyRaw.trim() ? Number(openingQtyRaw) : 0;
    if (!Number.isFinite(opening) || opening <= 0) return null;
    const others = entries.reduce(
      (sum, e, i) => (i === editIdx ? sum : sum + entryPiecesOnHand(e)),
      0,
    );
    const total = others + candidatePiecesOnHand;
    return total > opening
      ? `Bundles would total ${total} pcs but opening stock is ${opening} pcs — lower the quantity or raise the opening stock.`
      : null;
  };

  return (
    <>
      <StoreFormRow single>
        <FormField
          control={form.control}
          name="pricingEntries"
          render={({ field }) => (
            <FormItem>
              <PricingEntryList
                value={(field.value as PricingEntry[]) ?? []}
                onChange={field.onChange}
                taxPct={taxPct}
                // The Bundle Builder's size/colour sources (#235). Watched
                // rather than snapshotted so an axis added in the Attributes
                // section above is offerable here without a save in between.
                variantAttributes={form.watch("variantAttributes") ?? []}
                productVariants={form.watch("variants") ?? []}
                coverageGuard={coverageGuard}
              />
              <FormMessage />
            </FormItem>
          )}
        />
      </StoreFormRow>

      {legacy ? (
        <>
          <p className="text-xs text-muted">
            This product&apos;s composition was saved in an older format:{" "}
            <span className="font-mono text-ink-2">{sizeRatio}</span>. It can&apos;t be shown as a
            composition because it doesn&apos;t say which sizes it covers — add an entry above to
            replace it, or leave the count below as-is.
          </p>
          <StoreFormRow>
            <TextField
              name="piecesPerPack"
              label="Pieces per pack *"
              placeholder="6"
              inputMode="numeric"
              helper="Only needed while the older format is in place."
            />
          </StoreFormRow>
        </>
      ) : null}
    </>
  );
}
